import { jsxs, jsx } from "react/jsx-runtime";
import { cn } from "../../../lib/utils.js";
import "../shared/quiz-blank.js";
import { KatexText } from "../shared/katex-text.js";
import { QuizResultFeedback } from "../shared/quiz-result-feedback.js";
import { SubmitButton } from "../shared/submit-button.js";
import "react";
import { useTranslation } from "react-i18next";
import "../../../node_modules/.pnpm/hls.js@1.6.15/node_modules/hls.js/dist/hls.js";
import { QuizMedia } from "../shared/quiz-media.js";
import { QuizSolveDescription } from "../shared/quiz-solve-description.js";
import { DifficultyBadge, DebugQuizId } from "../shared/debug-quiz-id.js";
import { formatText } from "../../../utils/quiz-parser.js";
import { useCountingQuiz } from "../hooks/use-counting-quiz.js";
import Lottie from "../../../node_modules/.pnpm/lottie-react@2.4.1_react-dom@18.3.1_react@18.3.1__react@18.3.1/node_modules/lottie-react/build/index.es.js";
import countingAnimation from "../counting/assets/quiz_counting.json.js";
import { motion } from "../../../node_modules/.pnpm/framer-motion@11.18.2_react-dom@18.3.1_react@18.3.1__react@18.3.1/node_modules/framer-motion/dist/es/render/components/motion/proxy.js";
function CountingStudent({
  quizId,
  quiz,
  onSolve,
  solveResult,
  showReallyTrue = false,
  isViewSolved = false,
  initialAnswers: initialAnswersProp,
  submitLabel,
  hideSubmitButton = false,
  onAnswerChange,
  disableSound = false,
  difficultyLevel
}) {
  const { t } = useTranslation("quiz");
  const {
    displayValues,
    isSolved,
    canSubmit,
    shouldShowCorrectAnswers,
    isShowingCorrectAnswers,
    setIsShowingCorrectAnswers,
    correctDigits,
    showLottie,
    isDisabled,
    handleUp,
    handleDown,
    handleSubmit
  } = useCountingQuiz({
    quizId,
    quiz,
    onSolve,
    solveResult,
    showReallyTrue,
    isViewSolved,
    initialAnswers: initialAnswersProp,
    onAnswerChange
  });
  return /* @__PURE__ */ jsxs("div", { className: "space-y-6", children: [
    /* @__PURE__ */ jsx(
      motion.div,
      {
        initial: { opacity: 0, y: -10 },
        animate: { opacity: 1, y: 0 },
        className: "space-y-2",
        children: /* @__PURE__ */ jsxs("div", { className: "inline-flex gap-2 items-center", children: [
          /* @__PURE__ */ jsx("div", { className: "inline-flex gap-2 items-center px-3 py-1 text-sm font-bold text-white bg-[#55C3FF] rounded-lg", children: /* @__PURE__ */ jsx("span", { children: t("types.counting.label") }) }),
          /* @__PURE__ */ jsx(DifficultyBadge, { level: difficultyLevel }),
          /* @__PURE__ */ jsx(DebugQuizId, { quizId })
        ] })
      }
    ),
    /* @__PURE__ */ jsxs(
      motion.div,
      {
        initial: { opacity: 0 },
        animate: { opacity: 1 },
        transition: { delay: 0.1 },
        className: cn(
          "p-6 bg-linear-to-br to-gray-50 rounded-2xl from-slate-50",
          "border border-gray-100 shadow-2xs",
          "text-2xl font-medium leading-relaxed text-gray-900"
        ),
        children: [
          /* @__PURE__ */ jsx(KatexText, { text: quiz.title }),
          quiz.problem && (() => {
            const cleanProblem = quiz.problem.replace(/\{\{\}\}/g, "").trim();
            if (!cleanProblem) return null;
            return /* @__PURE__ */ jsx("div", { className: "mt-4 text-xl", children: formatText(cleanProblem, `${quizId}-problem`, quiz.additionalImages ?? void 0) });
          })(),
          /* @__PURE__ */ jsx(QuizMedia, { image: quiz.image, imageMobile: quiz.imageMobile, desmos: quiz.desmos, vars: quiz.vars })
        ]
      }
    ),
    /* @__PURE__ */ jsx("div", { className: "flex items-center justify-center gap-4 mt-6", children: displayValues.map((value, index) => /* @__PURE__ */ jsxs(
      motion.div,
      {
        initial: { opacity: 0, scale: 0.8 },
        animate: { opacity: 1, scale: 1 },
        transition: { delay: index * 0.1 },
        className: cn(
          "relative inline-flex flex-col items-center justify-center p-2 text-5xl font-semibold rounded-2xl",
          shouldShowCorrectAnswers || isSolved && (solveResult == null ? void 0 : solveResult.correct) ? "bg-[#D8F6EF] text-[#3BD0AE]" : isSolved && !(solveResult == null ? void 0 : solveResult.correct) ? "bg-[#FFF0EF] text-[#FF675E]" : "bg-[#DDF3FF] text-[#505050]"
        ),
        children: [
          /* @__PURE__ */ jsx(
            "button",
            {
              onClick: () => handleUp(index),
              disabled: isDisabled,
              className: cn(
                "w-20 h-10 rounded-xl flex items-center justify-center transition-all duration-150",
                !isDisabled ? "bg-[#55C3FF] [box-shadow:0px_3px_0px_#2E96CE] hover:bg-[#99DBFF] hover:[box-shadow:0px_3px_0px_#55C3FF] active:translate-y-px active:[box-shadow:unset]" : "bg-[#E0E0E0] [box-shadow:0px_3px_0px_#AAAAAA]"
              ),
              "aria-label": t("types.counting.incrementDigit", { position: index + 1 }),
              children: /* @__PURE__ */ jsx("svg", { width: "24", height: "24", viewBox: "0 0 24 24", fill: "none", xmlns: "http://www.w3.org/2000/svg", children: /* @__PURE__ */ jsx(
                "path",
                {
                  d: "M7.71515 15C6.96634 15 6.44661 14.6891 6.15596 14.0672C5.8642 13.4463 5.98179 12.8949 6.50873 12.413L10.8352 8.45652C11.0016 8.30435 11.1819 8.19022 11.376 8.11413C11.5701 8.03804 11.7781 8 12 8C12.2219 8 12.4299 8.03804 12.624 8.11413C12.8181 8.19022 12.9984 8.30435 13.1648 8.45652L17.4913 12.413C18.0182 12.8949 18.1358 13.4463 17.844 14.0672C17.5534 14.6891 17.0337 15 16.2849 15L7.71515 15Z",
                  fill: isDisabled ? "#AAAAAA" : "white"
                }
              ) })
            }
          ),
          showLottie ? /* @__PURE__ */ jsx("div", { className: "flex items-center justify-center", style: { width: 80, height: 88 }, children: /* @__PURE__ */ jsx(Lottie, { animationData: countingAnimation, loop: true, autoplay: true, style: { width: 80, height: 88 } }) }) : /* @__PURE__ */ jsx("span", { className: "flex items-center justify-center h-[88px]", children: value }),
          /* @__PURE__ */ jsx(
            "button",
            {
              onClick: () => handleDown(index),
              disabled: isDisabled,
              className: cn(
                "w-20 h-10 rounded-xl flex items-center justify-center transition-all duration-150",
                !isDisabled ? "bg-[#55C3FF] [box-shadow:0px_3px_0px_#2E96CE] hover:bg-[#99DBFF] hover:[box-shadow:0px_3px_0px_#55C3FF] active:translate-y-px active:[box-shadow:unset]" : "bg-[#E0E0E0] [box-shadow:0px_3px_0px_#AAAAAA]"
              ),
              "aria-label": t("types.counting.decrementDigit", { position: index + 1 }),
              children: /* @__PURE__ */ jsx("svg", { width: "24", height: "24", viewBox: "0 0 24 24", fill: "none", xmlns: "http://www.w3.org/2000/svg", className: "rotate-180", children: /* @__PURE__ */ jsx(
                "path",
                {
                  d: "M7.71515 15C6.96634 15 6.44661 14.6891 6.15596 14.0672C5.8642 13.4463 5.98179 12.8949 6.50873 12.413L10.8352 8.45652C11.0016 8.30435 11.1819 8.19022 11.376 8.11413C11.5701 8.03804 11.7781 8 12 8C12.2219 8 12.4299 8.03804 12.624 8.11413C12.8181 8.19022 12.9984 8.30435 13.1648 8.45652L17.4913 12.413C18.0182 12.8949 18.1358 13.4463 17.844 14.0672C17.5534 14.6891 17.0337 15 16.2849 15L7.71515 15Z",
                  fill: isDisabled ? "#AAAAAA" : "white"
                }
              ) })
            }
          )
        ]
      },
      index
    )) }),
    !isSolved && !isViewSolved && !hideSubmitButton && /* @__PURE__ */ jsx(
      SubmitButton,
      {
        onClick: handleSubmit,
        disabled: !canSubmit,
        label: submitLabel
      }
    ),
    isSolved && /* @__PURE__ */ jsx(
      QuizResultFeedback,
      {
        isCorrect: (solveResult == null ? void 0 : solveResult.correct) ?? false,
        show: isSolved,
        correctAnswer: shouldShowCorrectAnswers && correctDigits ? correctDigits.join("") : void 0,
        disableSound
      }
    ),
    isSolved && (solveResult == null ? void 0 : solveResult.description) && /* @__PURE__ */ jsx(
      QuizSolveDescription,
      {
        description: solveResult.description,
        commentaryImages: quiz.commentaryImages,
        vars: quiz.vars,
        id: `${quizId}-desc`,
        actionVisible: !solveResult.correct,
        actionLabel: isShowingCorrectAnswers ? t("common.viewMyAnswer") : t("common.viewCorrectAnswer"),
        onActionClick: () => setIsShowingCorrectAnswers((v) => !v)
      }
    )
  ] });
}
export {
  CountingStudent
};
//# sourceMappingURL=counting-student.js.map
